import React from 'react';
import { Navigate, useLocation, matchPath } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDefaultPermissions } from '../config/routeConfig';
import PushNotificationBanner from './PushNotificationBanner';

const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { currentUser, loading, canAccess } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  const path = location.pathname;
  const defaults = getDefaultPermissions();

  // Find the route pattern (if any) that matches the current path (handles dynamic segments)
  const matchedPattern = Object.keys(defaults).find(pattern => matchPath(pattern, path));

  // Derive the parent route path for detail pages (e.g. /lead-sessions/:sessionId → /lead-sessions).
  // If the DB config for a hidden detail page excludes a role that the parent page allows,
  // we fall back to the parent — "if you can see the list, you can open an item."
  const parentPattern = matchedPattern
    ? matchedPattern.split('/').slice(0, -1).join('/') || null
    : null;

  // Sole authority: canAccess() checks the AC config (saved DB → override defaults from routeConfig).
  // The allowedRoles prop is intentionally NOT used as a fallback so that AC settings
  // are fully respected and cannot be bypassed by hardcoded role lists.
  const allowed =
    canAccess(path) ||
    (matchedPattern && canAccess(matchedPattern)) ||
    (parentPattern && canAccess(parentPattern)) ||
    canAccess(path.replace(/\/[a-f0-9]{24}$/i, '/:id').replace(/\/PCL-[^/]+$/i, '/:parcelId'));

  if (allowed) {
    return (
      <>
        <PushNotificationBanner />
        {children}
      </>
    );
  }

  if (currentUser.role === 'broker') {
    return <Navigate to="/solitaire-procurement" replace />;
  }

  return <Navigate to="/messenger" replace />;
};

export default ProtectedRoute;
